import React, { useContext, useState } from "react";
import { Box, Flex, Grid, Image } from "theme-ui";

import { CountryStates } from "helpers/CountryStates";

import LogoUpload from "./LogoUpload";

import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { updateProfile } from "api/Users";
import { useToast } from "components/toast";
import UserContext from "context/User";
import H3 from "components/typography/H3";
import SelectInput from "components/forms/SelectInput";
import Separator from "components/ui/Separator";
import Subtext from "components/typography/Subtext";
import H5 from "components/typography/H5";

export default function GeneralSettings({
  clientCompany,
  values,
  errors,
  submitCount,
  setFieldValue,
  initClientCompany,
}: {
  clientCompany: any;
  values: any;
  errors: any;
  submitCount: number;
  setFieldValue: (i: string, v: any) => void;
  initClientCompany: () => void;
}) {
  const toast = useToast();
  const { setUser } = useContext<any>(UserContext);
  const [isRemoving, setIsRemoving] = useState(false);
  
  const countryStates: any[] = Object.entries(CountryStates["US"]?.states || {}).map(([key, value]) => ({ value: key, label: value.name }));

  // Get logo from owner's user_data
  const ownerLogo = clientCompany?.owner?.data?.find((d: any) => d.key === "logo")?.value;

  const handleRemoveLogo = async () => {
    setIsRemoving(true);
    try {
      const res = await updateProfile({ 
        update: { 
          data: [{ key: "logo", value: "", type: "image" }] 
        } 
      });
      if (res?.id) {
        toast.add("Logo removed", "var(--theme-ui-colors-success)");
        setUser(res);
        initClientCompany();
      } else {
        toast.add(res?.message || "Error removing logo", "var(--theme-ui-colors-danger)");
      }
    } catch (err) {
      toast.add("Error removing logo", "var(--theme-ui-colors-danger)");
    }
    setIsRemoving(false);
  };

  return (
    <>
      <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <H3 marginTop={"10px"} marginBottom={0} fontSize={"16px"} fontWeight={"600"} fontFamily={"Poppins"}>
          General
        </H3>
      </Box>
      <Separator />
      
      {/* Basic Info */}
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Basic Info
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Edit Company Name and Phone.
          </Subtext>
        </Box>
        <Flex sx={{ gap: "1rem" }}>
          <TextInput
            name="name"
            label="Company Name"
            value={values.name}
            onChange={(val: any) => setFieldValue("name", val)}
            $errors={errors.name && submitCount > 0 ? errors.name : null}
            $customStyles={{ flexGrow: 2 }}
          />
          <TextInput 
            name="phone" 
            label="Phone" 
            value={values?.phone} 
            onChange={(val: any) => setFieldValue("phone", val)} 
            $customStyles={{ flexGrow: 1 }} 
          />
        </Flex>
      </Grid>
      <Separator />
      
      {/* Address */}
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Address
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Edit Company Address.
          </Subtext>
        </Box>
        <Box>
          <Flex sx={{ gap: "1rem" }}>
            <TextInput
              name="address[line1]"
              label="Address 1"
              value={values?.address?.line1}
              onChange={(val: any) => setFieldValue("address.line1", val)}
              $customStyles={{ flexGrow: 2 }}
            />
            <TextInput
              name="address[line2]"
              label="Address 2"
              value={values?.address?.line2}
              onChange={(val: any) => setFieldValue("address.line2", val)}
              $customStyles={{ flexGrow: 1 }}
            />
          </Flex>
          <Flex sx={{ gap: "1rem" }}>
            <TextInput
              name="address[city]"
              label="City"
              value={values?.address?.city}
              onChange={(val: any) => setFieldValue("address.city", val)}
              $customStyles={{ width: "33.3333%" }}
            />
            <SelectInput
              name="address[state]"
              label="State"
              value={values?.address?.state}
              options={countryStates}
              onChange={(val: any) => setFieldValue("address.state", val)}
              $customStyles={{ width: "33.3333%" }}
            />
            <TextInput 
              name="address[postalCode]" 
              label="Zip" 
              value={values?.address?.postalCode} 
              onChange={(val: any) => setFieldValue("address.postalCode", val)} 
              $customStyles={{ width: "33.3333%" }} 
            />
          </Flex>
        </Box>
      </Grid>
      <Separator />
      
      {/* Contact Information */}
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Contact Information
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Edit contact and billing email addresses.
          </Subtext>
        </Box>
        <Flex sx={{ gap: "1rem" }}>
          <TextInput
            name="contactEmail"
            label="Contact Email"
            value={values?.contactEmail}
            onChange={(val: any) => setFieldValue("contactEmail", val)}
            $customStyles={{ flexGrow: 1 }}
          />
          <TextInput
            name="billingEmail"
            label="Billing Email"
            value={values?.billingEmail}
            onChange={(val: any) => setFieldValue("billingEmail", val)}
            $customStyles={{ flexGrow: 1 }}
          />
        </Flex>
      </Grid>
      <Separator />
      
      {/* Company Logo */}
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Company Logo
          </H5>
          <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Update your company logo.
          </Subtext>
        </Box>
        <Flex sx={{ gap: "1rem", alignItems: "center" }}>
          {ownerLogo && <Image src={ownerLogo} width={75} />}
          <LogoUpload 
            clientCompany={clientCompany} 
            title={ownerLogo ? "Replace Logo" : "Upload Logo"} 
            afterUpload={(res) => {
              setUser(res);
              initClientCompany();
            }} 
          />
          {ownerLogo && (
            <BasicButton 
              scheme="dangerSecondary" 
              onClick={handleRemoveLogo}
              $submitting={isRemoving}
            >
              Remove
            </BasicButton>
          )}
        </Flex>
      </Grid>
    </>
  );
}
